import { Injectable } from '@angular/core';
import { ColumnRepository, DataRepository, TableRepository, WorkspaceDatabaseKeys } from '@database/workspace';
import { StandardTableProvider, BusinessLayerService, SortingService, SamplesService } from '@domain/business-layer';
import { TableExpandableCards } from '@domain/file-exporter';
import { SortingState, SortingStatus } from '@domain/sorting';
import {
	ColumnModel,
	DataModel,
	ExpandableCard,
	fileBooleanMappings,
	GroupedColumnsModel,
	listToDictionaryObject,
	getPrimaryKeys,
	PrimaryKeyValueModel,
	TableModel,
	ExpandableCardProperty,
	CardContentModel,
	DateTimeService,
	YesNoEnum
} from '@shared';
import { cloneDeep } from 'lodash';
import { combineLatest, forkJoin, Observable, of, switchMap, map, first } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class TableService {
	public constructor(
		private columnRepository: ColumnRepository,
		private tableRepository: TableRepository,
		private standardTableProvider: StandardTableProvider,
		private businessLayerService: BusinessLayerService,
		private sortingState: SortingState,
		private sortedDataService: SortingService,
		private sampleService: SamplesService,
		private dateTimeService: DateTimeService,
		private dataRepository: DataRepository
	) {}

	public getChildTables$(table: string): Observable<TableModel[]> {
		return this.tableRepository
			.getTableChildIdsByName$(table)
			.pipe(
				switchMap((childTableIds: number[] | null) =>
					childTableIds !== null ? this.tableRepository.getChildTablesMetadataByIds$(childTableIds) : of([])
				)
			);
	}

	public getChildTableFields$(
		workfileId: string,
		projectNumber: string,
		table: string,
		entryId: number,
		childTableName: string,
		shouldIncludePrimaryKeys: boolean,
		sampleTypes?: { [key: string]: boolean }
	): Observable<TableExpandableCards | null> {
		return this.sortingState.getTableSortState(childTableName).pipe(
			switchMap((sortingStatus: SortingStatus) =>
				this.businessLayerService.getPrimaryKeyAndValue$(workfileId, projectNumber, table, entryId).pipe(
					switchMap((primaryKeyValue: PrimaryKeyValueModel[]) =>
						this.sortedDataService
							.getSortedChildData$(childTableName, projectNumber, primaryKeyValue, {
								direction: sortingStatus.sortDirection,
								column: sortingStatus.selectedColumn
							})
							.pipe(
								switchMap((tableData: DataModel[]) => {
									if (tableData.length === 0) {
										return of({
											tableName: childTableName,
											expandableCards: []
										});
									}

									return this.columnRepository
										.getColumns$(childTableName)
										.pipe(
											map((dataColumns: ColumnModel[]) =>
												this.getChildTableDetails$(
													shouldIncludePrimaryKeys ? [] : getPrimaryKeys(primaryKeyValue),
													tableData,
													dataColumns,
													childTableName,
													sampleTypes
												)
											)
										);
								})
							)
					)
				)
			)
		);
	}

	public getChildTableFieldsWithStandardsForReportPreview$(
		workfileId: string,
		projectNumber: string,
		table: string,
		entryId: number,
		childTableName: string,
		shouldIncludePrimaryKeys: boolean,
		sampleTypes?: { [key: string]: boolean }
	): Observable<TableExpandableCards> {
		return this.getChildTableFieldsWithStandards$(
			workfileId,
			projectNumber,
			table,
			entryId,
			childTableName,
			shouldIncludePrimaryKeys,
			sampleTypes
		).pipe(
			switchMap((tableExpandableCards: TableExpandableCards) => {
				if (!tableExpandableCards) {
					return of({
						tableName: childTableName,
						expandableCards: []
					});
				}

				const getColumnsMetadata$: Observable<ColumnModel[]> = this.dataRepository
					.getColumnsMetadata$(childTableName)
					.pipe(first());
				const getPrimaryKeysAndValues$: Observable<PrimaryKeyValueModel[]> = this.businessLayerService
					.getPrimaryKeyAndValue$(workfileId, projectNumber, table, entryId)
					.pipe(first());

				return this.excludeNotDisplayedAndPrimaryKeysColumns$(
					getColumnsMetadata$,
					getPrimaryKeysAndValues$,
					childTableName,
					tableExpandableCards.expandableCards
				);
			})
		);
	}

	public getChildTableFieldsWithStandards$(
		workfileId: string,
		projectNumber: string,
		table: string,
		entryId: number,
		childTableName: string,
		shouldIncludePrimaryKeys: boolean,
		sampleTypes?: { [key: string]: boolean }
	): Observable<TableExpandableCards> {
		return this.sortingState.getTableSortState(childTableName).pipe(
			switchMap((sortingStatus: SortingStatus) =>
				this.businessLayerService.getPrimaryKeyAndValue$(workfileId, projectNumber, table, entryId).pipe(
					switchMap((primaryKeyValue: PrimaryKeyValueModel[]) =>
						this.sortedDataService
							.getCombinedSortedData(
								childTableName,
								this.standardTableProvider.standardTable,
								projectNumber,
								{
									direction: sortingStatus.sortDirection,
									column: sortingStatus.selectedColumn
								},
								primaryKeyValue
							)
							.pipe(
								switchMap((tableData: DataModel[]) => {
									if (tableData.length === 0) {
										return of({
											tableName: childTableName,
											expandableCards: []
										} as TableExpandableCards);
									}

									tableData.map((td: DataModel) => {
										const sanitizedTableData = cloneDeep(td);
										if (WorkspaceDatabaseKeys.columns.depthFrom in td) {
											delete sanitizedTableData[WorkspaceDatabaseKeys.columns.depthFrom];
											return sanitizedTableData;
										}
										if (WorkspaceDatabaseKeys.columns.depthTo in td) {
											delete sanitizedTableData[WorkspaceDatabaseKeys.columns.depthTo];
											return sanitizedTableData;
										}
									});

									return combineLatest([
										this.columnRepository.getColumns$(childTableName),
										this.columnRepository.getColumns$(this.standardTableProvider.standardTable)
									]).pipe(
										map((combinedColumns: [ColumnModel[], ColumnModel[]]) => {
											const groupedCols: GroupedColumnsModel = {
												sampleColumns: combinedColumns[0],
												standardColumns: combinedColumns[1]
											};

											return this.getChildTableSampleDetails$(
												shouldIncludePrimaryKeys ? [] : getPrimaryKeys(primaryKeyValue),
												tableData,
												groupedCols,
												childTableName,
												sampleTypes
											);
										})
									);
								})
							)
					)
				)
			)
		);
	}

	private getChildTableSampleDetails$(
		primaryKeys: string[],
		tableData: DataModel[],
		groupedColumns: GroupedColumnsModel,
		tableName: string,
		sampleTypes?: { [key: string]: boolean }
	): TableExpandableCards {
		if (tableData.length === 0) {
			return {
				tableName,
				expandableCards: []
			} as TableExpandableCards;
		}

		const excludedProperties = [WorkspaceDatabaseKeys.columns.projectNumber, ...primaryKeys];

		const expandableCards = tableData.map((td: DataModel) => {
			const isStandard = WorkspaceDatabaseKeys.columns.standardNumber in td;
			const sampleItems = this.sampleService
				.getSampleCardItems(groupedColumns, td, excludedProperties, isStandard)
				.map(
					(cardContent: CardContentModel) =>
						new ExpandableCardProperty({
							key: cardContent.key,
							value: cardContent.value,
							label: cardContent.label,
							order: cardContent.order,
							valueType: cardContent.valueType,
							isPrimaryKey: cardContent.isPrimaryKey
						})
				);

			return new ExpandableCard({
				id: td.rxdbid,
				properties: sampleItems,
				isMaster: fileBooleanMappings[td.is_master],
				isExpanded: false,
				isOriginalSample: sampleTypes ? sampleTypes[td.assay_sample_type_code] : null,
				isStandard
			});
		});

		return {
			tableName,
			expandableCards
		} as TableExpandableCards;
	}

	private getChildTableDetails$(
		primaryKeys: string[],
		tableData: DataModel[],
		dataColumns: ColumnModel[],
		tableName: string,
		sampleTypes?: { [key: string]: boolean }
	): TableExpandableCards {
		const dataVisibleColumns = dataColumns.filter(
			(dataColumn: ColumnModel) => fileBooleanMappings[dataColumn.display_column]
		);

		const dataColumnsDictionary = listToDictionaryObject(
			WorkspaceDatabaseKeys.columns.columnName,
			dataVisibleColumns
		);

		if (tableData.length === 0) {
			return {
				tableName,
				expandableCards: []
			} as TableExpandableCards;
		}

		const expandableCards = tableData.map((td: DataModel) => {
			const entryItems = dataVisibleColumns
				.filter(
					(dataColumn: ColumnModel) => !['project_number', ...primaryKeys].includes(dataColumn.column_name)
				)
				.filter((dataColumn: ColumnModel) => !dataColumnsDictionary[dataColumn.column_label])
				.map(
					(dataColumn: ColumnModel) =>
						new ExpandableCardProperty({
							key: dataColumn.column_name,
							value: td[dataColumn.column_name],
							label: dataColumn.column_label,
							order: dataColumn.display_order ? dataColumn.display_order : 0,
							valueType: this.dateTimeService.getColumnDataType(
								dataColumn.edit_style,
								dataColumn.data_type
							),
							isPrimaryKey: primaryKeys.includes(dataColumn.column_name)
						})
				)
				.sort((a, b) => a.order - b.order);

			return new ExpandableCard({
				id: td.rxdbid,
				properties: entryItems,
				isMaster: fileBooleanMappings[td.is_master],
				isExpanded: false,
				isOriginalSample: sampleTypes ? sampleTypes[td.assay_sample_type_code] : null,
				isStandard: WorkspaceDatabaseKeys.columns.standardNumber in td
			});
		});

		return {
			tableName,
			expandableCards
		} as TableExpandableCards;
	}

	private mapStandardRecordsToSampleRecords(
		tableName: string,
		expandableCards: ExpandableCard[],
		columnsMetadata: ColumnModel[]
	): TableExpandableCards {
		const emptyPlaceholderCardItem: ExpandableCardProperty = {
			key: '',
			value: '',
			label: '',
			order: 0,
			valueType: 'type',
			compare: () => true
		};

		const adaptedPropertiesExpandableCards = expandableCards.map((expandableCard: ExpandableCard) => {
			if (!expandableCard.isStandard) {
				return expandableCard;
			}

			const clonedColumns: ColumnModel[] = columnsMetadata.map((item: ColumnModel) => ({ ...item }));
			const expandableCardItemsDictionary = listToDictionaryObject('key', expandableCard.properties);

			// The object is destructered and it's label is changed due to the needs in the `entry-preview.component`
			// This case is needed when there are only standard records displayed in the `Samples` table
			// In order to not change the logic of the `entry-preview.component` too much, instead a solution in which the labels of
			// the standard records are changed to reflect sample records, but only at UI level
			// Check lines 30-32 inside `entry-preview.component.html`
			expandableCard.properties = clonedColumns.map((clonedColumn: ColumnModel) => {
				switch (clonedColumn.column_name) {
					case WorkspaceDatabaseKeys.columns.sampleNumber:
						return {
							...expandableCardItemsDictionary[WorkspaceDatabaseKeys.columns.standardNumber],
							key: clonedColumn.column_name,
							label: clonedColumn.column_label
						};
					case WorkspaceDatabaseKeys.referenceTables.assaySampleTypeCode:
						return {
							...expandableCardItemsDictionary[WorkspaceDatabaseKeys.referenceTables.assayStandardCode],
							key: clonedColumn.column_name,
							label: clonedColumn.column_label
						};
					case WorkspaceDatabaseKeys.columns.depthTo:
						return {
							...expandableCardItemsDictionary[WorkspaceDatabaseKeys.columns.insertedDepth],
							key: clonedColumn.column_name,
							label: clonedColumn.column_label
						};
					case WorkspaceDatabaseKeys.columns.depthFrom:
						return {
							...emptyPlaceholderCardItem,
							key: clonedColumn.column_name,
							label: clonedColumn.column_label,
							value: '0.0'
						};
					case WorkspaceDatabaseKeys.columns.lengthColumn:
						return {
							...emptyPlaceholderCardItem,
							key: clonedColumn.column_name,
							label: clonedColumn.column_label
						};
					default:
						return (
							{
								...expandableCardItemsDictionary[clonedColumn.column_name],
								key: clonedColumn.column_name,
								label: clonedColumn.column_label
							} ?? {
								...emptyPlaceholderCardItem,
								key: clonedColumn.column_name,
								label: clonedColumn.column_label
							}
						);
				}
			});
			return expandableCard;
		});

		return {
			tableName,
			expandableCards: adaptedPropertiesExpandableCards
		};
	}

	private excludeNotDisplayedAndPrimaryKeysColumns$(
		getColumnsMetadata$: Observable<ColumnModel[]>,
		getPrimaryKeysAndValues$: Observable<PrimaryKeyValueModel[]>,
		tableName: string,
		expandableCards: ExpandableCard[]
	): Observable<TableExpandableCards> {
		return forkJoin({
			columnsMetadata: getColumnsMetadata$,
			primaryKeysAndValues: getPrimaryKeysAndValues$
		}).pipe(
			switchMap(({ columnsMetadata, primaryKeysAndValues }) => {
				const primaryKeys: string[] = primaryKeysAndValues.map(
					(primaryKeyAndValue: PrimaryKeyValueModel) => primaryKeyAndValue.primaryKey
				);
				const displayedColumns = columnsMetadata.filter(
					(column: ColumnModel) =>
						column.display_column === YesNoEnum.Yes &&
						!primaryKeys.some((primaryKey: string) => primaryKey === column.column_name)
				);
				const sortedDisplayColumns = displayedColumns.sort((a, b) => a.display_order - b.display_order);
				return of(this.mapStandardRecordsToSampleRecords(tableName, expandableCards, sortedDisplayColumns));
			})
		);
	}
}
